import React, { useState, useEffect, useContext } from 'react';
import { Routes, Route, useNavigate, useMatch, Navigate } from 'react-router-dom';
import { AppProvider, AppContext } from './context/AppContext';
import SuiteTree from './components/SuiteTree';
import TestCaseList from './components/TestCaseList';
import TestCaseForm from './components/TestCaseForm';
import SuiteForm from './components/SuiteForm';
import LoginPage from './components/LoginPage';
import SettingsPage from './components/SettingsPage';
import { api } from './api';
import './App.css';

function RequireAuth({ children }) {
  const { currentUser, authLoading } = useContext(AppContext);
  if (authLoading) return <div className="loading">Loading...</div>;
  if (!currentUser) return <Navigate to="/login" replace />;
  return children;
}

function Header({ projects, setProjects, onImportDone }) {
  const navigate = useNavigate();
  const match = useMatch('/projects/:projectId/*');
  const projectId = match ? Number(match.params.projectId) : null;
  const suiteMatch = useMatch('/projects/:projectId/suites/:suiteId/*');
  const suiteId = suiteMatch ? Number(suiteMatch.params.suiteId) : null;
  const { currentUser, logout } = useContext(AppContext);

  const [newProjectName, setNewProjectName] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const fileInputRef = React.useRef();

  const currentProject = projects.find((p) => p.id === projectId) || null;
  const firstProject = projects[0] || null;

  const handleLogoClick = () => {
    if (firstProject) navigate(`/projects/${firstProject.id}`);
  };

  const handleProjectChange = (e) => {
    if (e.target.value) navigate(`/projects/${e.target.value}`);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    const p = await api.createProject({ name: newProjectName.trim() });
    setProjects((prev) => [...prev, p]);
    setNewProjectName('');
    setShowNewForm(false);
    navigate(`/projects/${p.id}`);
  };

  const handleDelete = async () => {
    if (!currentProject) return;
    if (!window.confirm(`Delete project "${currentProject.name}" and all its data?`)) return;
    await api.deleteProject(currentProject.id);
    const remaining = projects.filter((p) => p.id !== currentProject.id);
    setProjects(remaining);
    if (remaining.length > 0) navigate(`/projects/${remaining[0].id}`);
    else navigate('/');
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !projectId) return;
    e.target.value = '';
    setImporting(true);
    setImportMsg('');
    try {
      const result = await api.importFile(projectId, file, suiteId);
      setImportMsg(`Imported ${result.imported} test cases`);
      onImportDone();
      setTimeout(() => setImportMsg(''), 4000);
    } catch (err) {
      setImportMsg('Error: ' + err.message);
      setTimeout(() => setImportMsg(''), 5000);
    } finally {
      setImporting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="header">
      <h1 onClick={handleLogoClick} style={{ cursor: firstProject ? 'pointer' : 'default' }}>
        Test Case Manager MVP
      </h1>
      <div className="project-selector">
        <select value={projectId || ''} onChange={handleProjectChange}>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {currentProject && (
          <button className="btn-delete-project" onClick={handleDelete} title="Delete project">×</button>
        )}
        {showNewForm ? (
          <form onSubmit={handleCreate} className="new-project-form">
            <input
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Project name"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Escape') { setShowNewForm(false); setNewProjectName(''); } }}
            />
            <button type="submit">+</button>
            <button type="button" onClick={() => { setShowNewForm(false); setNewProjectName(''); }}
              style={{ background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
          </form>
        ) : (
          <button className="btn-new-project" onClick={() => setShowNewForm(true)}>+ New project</button>
        )}
      </div>
      <div className="header-user">
        {projectId && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
            <button
              className="btn-new-project"
              onClick={() => fileInputRef.current.click()}
              disabled={importing}
              title="Import test cases from .xlsx or .csv"
            >
              {importing ? 'Importing...' : 'Import'}
            </button>
            {importMsg && <span className="import-msg">{importMsg}</span>}
          </>
        )}
        <span className="header-nickname" onClick={() => navigate('/settings')} title="Settings">
          {currentUser?.nickname}
        </span>
      </div>
    </header>
  );
}

function TwoColumnLayout() {
  const [leftWidth, setLeftWidth] = useState(250);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = () => setIsDragging(true);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newWidth = Math.max(150, Math.min(500, e.clientX));
      setLeftWidth(newWidth);
    };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div className="main-layout" style={{ cursor: isDragging ? 'col-resize' : 'auto' }}>
      <div className="panel panel-left" style={{ width: leftWidth }}>
        <SuiteTree />
      </div>
      <div className="resize-handle" onMouseDown={handleMouseDown} />
      <div className="panel panel-center">
        <TestCaseList />
      </div>
    </div>
  );
}

function FullscreenLayout({ isNew, SuiteForm: SuiteFormProp }) {
  return (
    <div className="main-layout">
      <div className="panel panel-fullscreen">
        {SuiteFormProp ? <SuiteFormProp /> : <TestCaseForm isNew={isNew} />}
      </div>
    </div>
  );
}

function NoProjects() {
  return (
    <div className="main-layout">
      <div className="tc-empty" style={{ margin: 'auto', padding: 40 }}>
        Create your first project to get started
      </div>
    </div>
  );
}

function AppShell() {
  const { currentUser, refreshKey, refresh } = useContext(AppContext);
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    api.getProjects()
      .then((p) => { setProjects(p); setProjectsLoaded(true); })
      .catch(() => setProjectsLoaded(true));
  }, [currentUser, refreshKey]);

  return (
    <div className="app">
      <Header projects={projects} setProjects={setProjects} onImportDone={refresh} />
      <Routes>
        <Route path="/" element={
          !projectsLoaded
            ? <div className="loading">Loading...</div>
            : projects.length > 0
              ? <Navigate to={`/projects/${projects[0].id}`} replace />
              : <NoProjects />
        } />
        <Route path="/projects/:projectId" element={<TwoColumnLayout />} />
        <Route path="/projects/:projectId/suites/:suiteId" element={<TwoColumnLayout />} />
        <Route path="/projects/:projectId/suites/:suiteId/edit" element={<FullscreenLayout SuiteForm={SuiteForm} />} />
        <Route path="/projects/:projectId/suites/:suiteId/cases/new" element={<FullscreenLayout isNew />} />
        <Route path="/projects/:projectId/suites/:suiteId/cases/:caseId" element={<FullscreenLayout />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/*" element={<RequireAuth><AppShell /></RequireAuth>} />
      </Routes>
    </AppProvider>
  );
}

function LoginRoute() {
  const { currentUser, authLoading } = useContext(AppContext);
  if (authLoading) return <div className="loading">Loading...</div>;
  if (currentUser) return <Navigate to="/" replace />;
  return (
    <div className="app">
      <LoginPage />
    </div>
  );
}
